import { Component, Inject, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { UserProfileService } from '../../../user-profile.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CrudModeEnum } from '../../../../../../../core/base/enum/crud-mode.enum';
import icClose from '@iconify/icons-ic/twotone-close';
import { Observable, Subscription } from 'rxjs';
import { EstablishmentModel } from '../../../../../../../core/models/establishment.model';
import { EstablishmentService } from '../../../../establishment/establishment.service';
import { distinctUntilChanged, map, skip, startWith, tap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { StringHelper } from '../../../../../../../core/helpers/string.helper';

const ESTABLISHMENT_NOT_FOUND_MESSAGE = 'Cet établissement n’existe pas.';
const ESTABLISHMENT_ALREADY_AFFECTED_MESSAGE = 'Cet établissement est déjà affecté.';

@Component({
  selector: 'vex-user-profile-details-resume-affectation-form',
  templateUrl: './user-profile-details-resume-affectation-form.component.html',
  styleUrls: ['./user-profile-details-resume-affectation-form.component.scss'],
})
export class UserProfileDetailsResumeAffectationFormComponent implements OnInit {
  form: UntypedFormGroup;
  mode: CrudModeEnum = CrudModeEnum.Create;

  establishments$: Observable<Array<EstablishmentModel>>;

  title: string;

  icClose = icClose;

  userProfileId: string;
  loading = false;

  defaults: any;

  activities: Array<{ _id: string; label: string }> = [];
  selectedActivities: Array<{ _id: string; label: string }> = [];
  /** Activités de l'affectation archivées dans l'établissement : non proposées mais conservées à l'enregistrement */
  private archivedActivities: Array<{ _id: string; label: string }> = [];
  activitiesLoading = false;
  noActivitiesConfigured = false;
  establishmentSearchMessage: string | null = null;

  /** Établissements déjà affectés à l'utilisateur, hors celui de l'affectation en cours de modification */
  private otherAffectedEstablishments: Array<EstablishmentModel> = [];
  private activitiesSubscription: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<UserProfileDetailsResumeAffectationFormComponent>,
    public service: UserProfileService,
    private establishmentService: EstablishmentService,
    private activatedRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.mode = this.data.mode;
    this.title = this.data.title;
    this.userProfileId = this.data.userProfile._id;

    this.defaults = this.data.defaults;

    this.otherAffectedEstablishments = (this.data.userProfile?.affectations || [])
      .map(affectation => affectation.establishment)
      .filter(establishment => !this.isUpdateMode() || establishment._id !== this.defaults?.establishment?._id);

    this.establishmentChange();

    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(this.defaults?._id),
      establishment: new UntypedFormControl(this.defaults?.establishment, Validators.required),
      activities: new UntypedFormControl(this.defaults?.activities || []),
    });

    // Pendant la frappe, la valeur du champ est le texte saisi (string) : on ne charge les activités
    // qu'une fois un établissement sélectionné (objet), et une seule fois par établissement.
    // skip(1) : l'établissement initial (mode update) est chargé plus bas avec ses activités présélectionnées.
    const establishmentControl = this.form.controls.establishment;
    establishmentControl.valueChanges
      .pipe(
        startWith(establishmentControl.value),
        map(value => value?._id ?? null),
        distinctUntilChanged(),
        skip(1),
      )
      .subscribe(establishmentId => {
        if (establishmentId) {
          this.establishmentSearchMessage = null;
          this.loadActivitiesForEstablishment(establishmentId);
        } else {
          this.resetActivities();
        }
      });

    if (this.isUpdateMode() && this.defaults?.establishment?._id) {
      this.loadActivitiesForEstablishment(this.defaults.establishment._id, this.defaults.activities);
    }
  }

  private resetActivities() {
    // Annule un chargement en cours : sa réponse ne doit pas s'afficher pour un autre établissement
    this.activitiesSubscription?.unsubscribe();
    this.activitiesLoading = false;
    this.noActivitiesConfigured = false;
    this.activities = [];
    this.selectedActivities = [];
    this.archivedActivities = [];
    this.form.controls.activities.setValue([]);
  }

  private updateActivitiesControl(): void {
    this.form.controls.activities.setValue([...this.selectedActivities, ...this.archivedActivities]);
  }

  loadActivitiesForEstablishment(establishmentId: string, preSelected?: Array<{ _id: string; label: string }>) {
    this.resetActivities();
    this.activitiesLoading = true;

    this.activitiesSubscription = this.establishmentService.findById(establishmentId).subscribe(
      res => {
        const establishment = res.data;
        const establishmentActivities = (establishment.activities || []).map(a => ({ _id: a._id, label: a.label }));
        this.archivedActivities = (preSelected || []).filter(
          selected => !establishmentActivities.some(a => a._id === selected._id),
        );

        if (establishmentActivities.length === 0) {
          this.noActivitiesConfigured = true;
          this.updateActivitiesControl();
        } else {
          this.activities = establishmentActivities;

          if (preSelected && preSelected.length > 0) {
            const preSelectedIds = preSelected.map(a => a._id);
            this.selectedActivities = establishmentActivities.filter(a => preSelectedIds.includes(a._id));
          } else {
            this.selectedActivities = [...establishmentActivities];
          }

          this.updateActivitiesControl();
        }

        this.activitiesLoading = false;
      },
      () => {
        this.activitiesLoading = false;
      },
    );
  }

  areAllActivitiesSelected(): boolean {
    return this.activities.length > 0 && this.selectedActivities.length === this.activities.length;
  }

  areSomeActivitiesSelected(): boolean {
    return this.selectedActivities.length > 0 && this.selectedActivities.length < this.activities.length;
  }

  onToggleAll(checked: boolean): void {
    this.selectedActivities = checked ? [...this.activities] : [];
    this.updateActivitiesControl();
  }

  isActivitySelected(activity: { _id: string; label: string }): boolean {
    return this.selectedActivities.some(a => a._id === activity._id);
  }

  onActivityToggle(activity: { _id: string; label: string }, checked: boolean): void {
    if (checked) {
      this.selectedActivities = [...this.selectedActivities, activity];
    } else {
      this.selectedActivities = this.selectedActivities.filter(a => a._id !== activity._id);
    }
    this.updateActivitiesControl();
  }

  createItem() {
    if (this.form.valid) {
      this.loading = true;
      this.service.addAffectation(this.userProfileId, this.form.value).subscribe(
        res => {
          this.dialogRef.close(res);
        },
        error => {
          console.log(error);
          this.loading = false;
        },
      );
    }
  }

  updateItem() {
    if (this.form.valid) {
      this.loading = true;
      this.service.updateAffectation(this.userProfileId, this.form.value).subscribe(
        res => {
          this.dialogRef.close(res);
        },
        error => {
          console.log(error);
          this.loading = false;
        },
      );
    }
  }

  deleteItem() {
    // Le contrôle métier « acteur achat » est effectué côté back par deleteAffectation (PRED-3206) :
    // l'ancien pré-check front nécessitait READ_ESTABLISHMENT et bloquait la suppression en spinner infini.
    this.loading = true;
    this.service.deleteAffectation(this.userProfileId, this.data.affectationId).subscribe(
      res => this.dialogRef.close(res),
      error => {
        this.loading = false;
        if (error?.error?.isErrorToDisplayForUser) {
          // Blocage métier : le snackbar est déjà affiché par ErrorCrudInterceptor
          this.dialogRef.close();
        }
      },
    );
  }

  close() {
    this.dialogRef.close();
  }

  save() {}

  isCreateMode() {
    return this.mode === CrudModeEnum.Create;
  }

  isUpdateMode() {
    return this.mode === CrudModeEnum.Update;
  }

  isDeleteMode(): boolean {
    return this.mode === CrudModeEnum.Delete;
  }

  establishmentChange(state: any = '') {
    const excludedIds = this.otherAffectedEstablishments.map(establishment => establishment._id);
    this.establishments$ = this.establishmentService.findAllAsSelect(true, 'label', 'asc', state, { excludedIds }).pipe(
      map(x => x.data),
      tap(establishments => this.onEstablishmentsSearched(establishments)),
    );
  }

  /**
   * Message selon le texte saisi (pas encore sélectionné). Les établissements déjà affectés sont exclus
   * de la recherche : sans ce contrôle, les taper afficherait « non existant ».
   * - déjà affecté : nom exact d'un établissement affecté, ou aucun résultat mais un affecté correspond ;
   * - non existant : aucun résultat.
   */
  private onEstablishmentsSearched(establishments: Array<EstablishmentModel>) {
    const value = this.form?.controls.establishment.value;
    const typed = typeof value === 'string' ? StringHelper.normalizeForSearch(value.trim()) : '';
    const noResult = establishments.length === 0;
    const affectedLabels = this.otherAffectedEstablishments.map(establishment =>
      StringHelper.normalizeForSearch(establishment.label?.trim()),
    );

    if (!typed) {
      this.establishmentSearchMessage = null;
    } else if (affectedLabels.includes(typed) || (noResult && affectedLabels.some(label => label.includes(typed)))) {
      this.establishmentSearchMessage = ESTABLISHMENT_ALREADY_AFFECTED_MESSAGE;
    } else {
      this.establishmentSearchMessage = noResult ? ESTABLISHMENT_NOT_FOUND_MESSAGE : null;
    }
  }
}
